import { useEffect, useState, type ReactNode } from 'react';
import { captureScreenshot, collectDiagnostics } from '../lib/diagnostics.js';
import { bugReports } from '../lib/tomo.js';

/**
 * Report a problem — the room's form, from the console's pages.
 *
 * Says out loud what is attached before anything is sent, for the same reason
 * the room does: collecting a user agent and a path is reasonable to send and
 * unreasonable to send silently.
 */
const ATTACHED = [
  'The page address, without its query string',
  'Your browser and window size',
];

export function BugReportModal({ onClose }: { readonly onClose: () => void }): ReactNode {
  const [text, setText] = useState('');
  const [shot, setShot] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); };
  }, [onClose]);

  const send = async (): Promise<void> => {
    if (text.trim().length === 0 || busy) return;
    setBusy(true);
    setFailed(false);
    try {
      await bugReports.send({
        description: text.trim(),
        diagnostics: collectDiagnostics(),
        screenshot: shot,
      });
      setSent(true);
    } catch {
      // The text is KEPT. Somebody who has just described a problem twice
      // does not describe it a third time.
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4" onMouseDown={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Report a problem"
        onMouseDown={(e) => { e.stopPropagation(); }}
        className="w-full max-w-md overflow-hidden rounded-xl border border-ink-800 bg-ink-900 text-ink-50"
      >
        <header className="flex items-center justify-between border-b border-ink-800 px-4 py-3">
          <h2 className="text-[14px] font-semibold">Report a problem</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded px-2 text-[13px] text-ink-400 hover:text-ink-50"
          >
            ✕
          </button>
        </header>

        {sent ? (
          <div className="px-4 py-6 text-center">
            <p className="text-[14px] font-semibold">Sent. Thank you.</p>
            <p className="mt-1 text-[12px] leading-4 text-ink-400">
              This goes straight to the people who build tomo.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-4 rounded bg-ink-800 px-3 py-1.5 text-[13px] font-medium"
            >
              Close
            </button>
          </div>
        ) : (
          <div className="px-4 py-4">
            <label htmlFor="bug-description" className="block text-[13px] text-ink-400">
              What went wrong?
            </label>
            <textarea
              id="bug-description"
              value={text}
              onChange={(e) => { setText(e.target.value); }}
              rows={4}
              maxLength={2000}
              autoFocus
              placeholder="The community I made will not load…"
              className="mt-1.5 w-full resize-none rounded border border-ink-800 bg-ink-950 px-3 py-2 text-[14px] leading-5 outline-none placeholder:text-ink-400 focus:border-ink-700"
            />

            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={() => { void captureScreenshot().then(setShot); }}
                disabled={busy}
                className="rounded bg-ink-800 px-3 py-1.5 text-[13px] font-medium disabled:text-ink-400"
              >
                {shot === null ? 'Add a screenshot' : 'Retake screenshot'}
              </button>
              {shot !== null && (
                <>
                  <span className="text-[12px] text-ink-400">Attached</span>
                  <button
                    type="button"
                    onClick={() => { setShot(null); }}
                    className="text-[12px] text-ink-400 underline hover:text-ink-50"
                  >
                    remove
                  </button>
                </>
              )}
            </div>

            {shot !== null && (
              // Shown, because nobody should send a picture of their screen
              // without having seen which picture it is.
              <img
                src={shot}
                alt="The screenshot that will be sent"
                className="mt-2 max-h-40 w-full rounded border border-ink-800 object-contain"
              />
            )}

            <details className="mt-3 rounded bg-ink-950 px-3 py-2">
              <summary className="cursor-pointer text-[12px] text-ink-400">
                Also sent, to help us find it
              </summary>
              <ul className="mt-1.5 space-y-0.5 text-[11px] leading-4 text-ink-400">
                {ATTACHED.map((line) => <li key={line}>{line}</li>)}
              </ul>
            </details>

            {failed && (
              <p role="alert" className="mt-2 text-[12px] text-warn-400">
                That did not send. Your description is still here — try again.
              </p>
            )}

            <div className="mt-3 flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded px-3 py-1.5 text-[13px] text-ink-400 hover:text-ink-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => { void send(); }}
                disabled={text.trim().length === 0 || busy}
                className="rounded bg-brand-500 px-3 py-1.5 text-[13px] font-semibold text-white disabled:bg-ink-800 disabled:text-ink-400"
              >
                {busy ? 'Sending…' : 'Send report'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
